/**
 * Screen 1: list of all sets. By default it only offers learning, so a child
 * cannot delete a set or wipe the progress by accident. Everything that manages
 * data is hidden behind a deliberate double tap on the gear button.
 */
import { el, confirmAction, page, setChildren } from '../dom';
import { navigate, render } from '../router';
import { deleteSet, getSets, resetProgress } from '../../core/storage';
import { downloadExport } from '../../core/exchange';
import { setProgress } from '../../core/leitner';
import { listGroups } from '../../core/groups';
import type { VocabSet } from '../../core/types';

/** How long the first tap on the gear button stays armed. */
const UNLOCK_WINDOW_MS = 2000;

let adminMode = false;
let armedUntil = 0;

/** Called by the router: leaving the start screen always locks it again. */
export function lockAdminMode(path: string): void {
  if (path === '/sets') return;
  adminMode = false;
  armedUntil = 0;
}

function progressBar(value: number): HTMLElement {
  const percent = Math.round(value * 100);
  return el(
    'div',
    {
      class: 'progress',
      role: 'progressbar',
      'aria-valuenow': percent,
      'aria-valuemin': 0,
      'aria-valuemax': 100,
      'aria-label': `Fortschritt ${percent} Prozent`,
    },
    [el('div', { class: 'progress-fill', style: `width:${percent}%` })],
  );
}

function metaText(set: VocabSet): string {
  const percent = Math.round(setProgress(set) * 100);
  const goals = listGroups(set).filter((group) => group.name !== '').length;
  const goalText = goals > 0 ? ` · ${goals} ${goals === 1 ? 'Lernziel' : 'Lernziele'}` : '';
  return `${set.cards.length} Wörter${goalText} · ${percent}% gelernt`;
}

/** The management row, only rendered while the admin mode is unlocked. */
function adminActions(set: VocabSet): HTMLElement {
  return el('div', { class: 'set-actions-secondary' }, [
    el('button', {
      class: 'btn btn-ghost',
      type: 'button',
      text: 'Bearbeiten',
      onclick: () => navigate(`/edit/${set.id}`),
    }),
    el('button', {
      class: 'btn btn-ghost',
      type: 'button',
      text: 'Export',
      onclick: () => downloadExport([set]),
    }),
    el('button', {
      class: 'btn btn-ghost',
      type: 'button',
      text: 'Reset',
      onclick: () => {
        if (!confirmAction(`Fortschritt von "${set.name}" wirklich zurücksetzen?`)) return;
        resetProgress(set.id);
        render();
      },
    }),
    el('button', {
      class: 'btn btn-ghost btn-delete',
      type: 'button',
      text: 'Löschen',
      onclick: () => {
        if (!confirmAction(`Set "${set.name}" mit allen Wörtern wirklich löschen?`)) return;
        deleteSet(set.id);
        render();
      },
    }),
  ]);
}

function setCard(set: VocabSet): HTMLElement {
  return el('article', { class: 'card set-card' }, [
    el('button', {
      class: 'set-main',
      type: 'button',
      onclick: () => navigate(`/learn/${set.id}`),
    }, [
      el('div', { class: 'set-head' }, [
        el('h2', { class: 'set-name', text: set.name }),
        el('span', { class: 'badge', text: `${set.nativeLang} → ${set.foreignLang}` }),
      ]),
      el('p', { class: 'set-meta', text: metaText(set) }),
      progressBar(setProgress(set)),
    ]),
    el('div', { class: 'set-actions' }, [
      el('button', {
        class: 'btn btn-primary btn-big',
        type: 'button',
        text: 'Lernen',
        onclick: () => navigate(`/learn/${set.id}`),
      }),
      el('div', { class: 'set-actions-secondary' }, [
        el('button', {
          class: 'btn btn-ghost',
          type: 'button',
          text: 'Statistik',
          onclick: () => navigate(`/stats/${set.id}`),
        }),
      ]),
      adminMode ? adminActions(set) : null,
    ]),
  ]);
}

/**
 * Gear button. The first tap only arms it and says so, the second tap within
 * `UNLOCK_WINDOW_MS` unlocks - deliberate enough that it does not happen by
 * accident, but still findable without instructions.
 */
function adminToggle(hint: HTMLElement): HTMLElement {
  const button = el('button', {
    class: `btn btn-ghost btn-gear${adminMode ? ' btn-gear-active' : ''}`,
    type: 'button',
    title: 'Verwaltung',
    'aria-label': 'Verwaltung',
    'aria-pressed': adminMode,
    text: '\u2699\ufe0e',
  });

  button.addEventListener('click', () => {
    if (adminMode) {
      adminMode = false;
      armedUntil = 0;
      render();
      return;
    }

    if (Date.now() < armedUntil) {
      adminMode = true;
      armedUntil = 0;
      render();
      return;
    }

    armedUntil = Date.now() + UNLOCK_WINDOW_MS;
    button.classList.add('btn-gear-armed');
    setChildren(hint, ['Nochmals tippen für die Verwaltung']);

    window.setTimeout(() => {
      if (adminMode) return;
      armedUntil = 0;
      button.classList.remove('btn-gear-armed');
      hint.replaceChildren();
    }, UNLOCK_WINDOW_MS);
  });

  return button;
}

function adminBanner(): HTMLElement {
  return el('div', { class: 'card admin-banner' }, [
    el('p', { class: 'admin-banner-text', text: 'Verwaltung ist offen.' }),
    el('button', {
      class: 'btn btn-ghost',
      type: 'button',
      text: 'Fertig',
      onclick: () => {
        adminMode = false;
        render();
      },
    }),
  ]);
}

export function setsScreen(): HTMLElement {
  const sets = getSets();
  const hint = el('p', { class: 'admin-hint', role: 'status' });

  const empty = el('div', { class: 'card empty' }, [
    el('p', { class: 'empty-title', text: 'Noch keine Wörter da.' }),
    el('p', { text: 'Füge dein erstes Set ein - kopiere die Wörterliste einfach hinein.' }),
    el('button', {
      class: 'btn btn-primary btn-big',
      type: 'button',
      text: 'Wörter einfügen',
      onclick: () => navigate('/import'),
    }),
  ]);

  return page({
    title: 'Voci',
    actions: [adminToggle(hint)],
    children: [
      hint,
      adminMode ? adminBanner() : null,
      sets.length === 0 ? empty : el('div', { class: 'set-list' }, sets.map(setCard)),
      adminMode && sets.length > 0
        ? el('div', { class: 'bottom-actions' }, [
            el('button', {
              class: 'btn btn-primary btn-big',
              type: 'button',
              text: '+ Neues Set',
              onclick: () => navigate('/import'),
            }),
            el('button', {
              class: 'btn btn-ghost btn-big',
              type: 'button',
              text: 'Alle exportieren',
              onclick: () => downloadExport(sets),
            }),
            el('button', {
              class: 'btn btn-ghost btn-big',
              type: 'button',
              text: 'Datei importieren',
              onclick: () => navigate('/transfer'),
            }),
          ])
        : null,
    ],
  });
}
